import React, { useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Download,
  ExpandLess,
  ExpandMore,
  KeyboardReturn,
} from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useAuth from "../../../hooks/useAuth";
import { equipmentInventoryService } from "../../../api/equipmentInventoryService";
import { EquipmentInventoryList } from "../../../interfaces/Fleet/EquipmentInventory";

const money = (value: number) =>
  value.toLocaleString("en-US", { style: "currency", currency: "USD" });
const dateTime = (value: string) =>
  new Date(value).toLocaleString("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  });
const conditionLabel = (value: string | number) =>
  typeof value === "number"
    ? (["New", "Used", "Damaged"][value] ?? value)
    : value;
const errorMessage = (error: unknown) =>
  axios.isAxiosError(error)
    ? error.response?.data?.detail ||
      error.response?.data?.title ||
      error.message
    : "Something went wrong. Please try again.";

export default function EquipmentInventoryLists({
  driverId,
}: {
  driverId?: string;
}) {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState<string>();
  const [returnList, setReturnList] = useState<EquipmentInventoryList>();
  const [received, setReceived] = useState<Record<string, string>>({});

  const lists = useQuery({
    queryKey: ["equipment-inventory-lists", driverId],
    queryFn: () => equipmentInventoryService.getLists(token, driverId),
  });
  const returnMutation = useMutation({
    mutationFn: () => {
      const items = Object.entries(received)
        .filter(([, quantity]) => Number(quantity) > 0)
        .map(([inventoryListItemId, quantityReceived]) => ({
          inventoryListItemId,
          quantityReceived: Number(quantityReceived),
        }));
      if (!items.length)
        throw new Error("Enter a positive quantity for at least one item.");
      return equipmentInventoryService.returnItems(
        token,
        returnList!.id,
        items,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["equipment-inventory"] });
      queryClient.invalidateQueries({
        queryKey: ["equipment-inventory-lists"],
      });
      setReturnList(undefined);
      setReceived({});
    },
  });
  const localError = useMemo(() => {
    if (!returnList) return "";
    for (const item of returnList.items) {
      const qty = received[item.id];
      if (qty && (!Number.isInteger(Number(qty)) || Number(qty) < 0))
        return `${item.equipmentName}: use a positive whole number.`;
      if (Number(qty) > item.quantityOutstanding)
        return `${item.equipmentName}: cannot return more than ${item.quantityOutstanding}.`;
    }
    return "";
  }, [received, returnList]);

  if (lists.isLoading)
    return (
      <Typography color="text.secondary">Loading inventory lists…</Typography>
    );
  if (lists.isError)
    return <Alert severity="error">{errorMessage(lists.error)}</Alert>;
  if (!lists.data?.length)
    return (
      <Alert severity="info">
        No equipment inventory lists have been saved
        {driverId ? " for this driver" : ""}.
      </Alert>
    );

  return (
    <Stack spacing={2}>
      {lists.data.map((list) => (
        <Paper key={list.id} variant="outlined" sx={{ overflow: "hidden" }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ md: "center" }}
            spacing={2}
            sx={{ p: 2 }}
          >
            <IconButton
              onClick={() =>
                setExpanded(expanded === list.id ? undefined : list.id)
              }
              aria-label="show inventory details"
            >
              {expanded === list.id ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
            <Box sx={{ flex: 1 }}>
              <Typography fontWeight={700}>{list.driverName}</Typography>
              <Typography variant="body2" color="text.secondary">
                Truck {list.truckNumber} · Trailer {list.trailerNumber} · Issued{" "}
                {dateTime(list.issuedAt)}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip label={`${list.totalIssued} issued`} />
              <Chip
                label={`${list.totalReturned} returned`}
                color="success"
                variant="outlined"
              />
              <Chip
                label={`${list.totalOutstanding} outstanding · ${money(list.totalOutstandingValue)}`}
                color={list.totalOutstanding ? "warning" : "success"}
              />
            </Stack>
            <Tooltip title="Download latest PDF">
              <IconButton
                onClick={() =>
                  equipmentInventoryService.downloadPdf(token, list.id)
                }
              >
                <Download />
              </IconButton>
            </Tooltip>
            {list.totalOutstanding > 0 && (
              <Button
                startIcon={<KeyboardReturn />}
                onClick={() => {
                  setReturnList(list);
                  setReceived({});
                }}
              >
                Receive return
              </Button>
            )}
          </Stack>
          <Collapse in={expanded === list.id}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Equipment</TableCell>
                    <TableCell>Condition</TableCell>
                    <TableCell align="right">Unit price</TableCell>
                    <TableCell align="right">Issued</TableCell>
                    <TableCell align="right">Returned</TableCell>
                    <TableCell align="right">Outstanding</TableCell>
                    <TableCell align="right">Outstanding value</TableCell>
                    <TableCell>Return history</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {list.items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.equipmentName}</TableCell>
                      <TableCell>{conditionLabel(item.condition)}</TableCell>
                      <TableCell align="right">
                        {money(item.unitPrice)}
                      </TableCell>
                      <TableCell align="right">{item.quantityIssued}</TableCell>
                      <TableCell align="right">
                        {item.quantityReturned}
                      </TableCell>
                      <TableCell align="right">
                        <strong>{item.quantityOutstanding}</strong>
                      </TableCell>
                      <TableCell align="right">
                        {money(item.outstandingValue)}
                      </TableCell>
                      <TableCell>
                        {item.returnHistory.length
                          ? item.returnHistory.map((r) => (
                              <Typography
                                variant="caption"
                                display="block"
                                key={r.id}
                              >
                                {r.quantityReceived} on {dateTime(r.returnedAt)}
                              </Typography>
                            ))
                          : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Collapse>
        </Paper>
      ))}
      <Dialog
        open={!!returnList}
        onClose={() => setReturnList(undefined)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Receive equipment return</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Enter quantities received now. Previous partial returns remain in
            the history.
          </Typography>
          <Stack spacing={2}>
            {returnList?.items
              .filter((i) => i.quantityOutstanding > 0)
              .map((item) => (
                <TextField
                  key={item.id}
                  label={`${item.equipmentName} (${item.quantityOutstanding} outstanding)`}
                  type="number"
                  value={received[item.id] || ""}
                  onChange={(e) =>
                    setReceived({ ...received, [item.id]: e.target.value })
                  }
                  inputProps={{
                    min: 0,
                    max: item.quantityOutstanding,
                    step: 1,
                  }}
                />
              ))}
          </Stack>
          {(localError || returnMutation.isError) && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {localError || errorMessage(returnMutation.error)}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReturnList(undefined)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!!localError || returnMutation.isPending}
            onClick={() => returnMutation.mutate()}
          >
            Save return
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
